import { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { QRCodeCanvas } from 'qrcode.react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Download, Share2, CheckCircle2, Sparkles, Copy, FileText } from 'lucide-react';
import useStore from '@/store/useStore';
import { apiDownloadInvoicePdf } from '@/services/api';

export default function QrCodeModal({ open, onClose }) {
  const { merchantUser } = useStore();
  const restaurantName = merchantUser?.restaurant_name || 'Restaurant';
  const restaurantLogo = merchantUser?.logo_base64;
  const merchantId = merchantUser?.id || 'demo';
  const [shareMessage, setShareMessage] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const qrCardRef = useRef(null);

  const storeUrl = (() => {
    if (typeof window === 'undefined') return '';
    const configuredPublicBase = (process.env.REACT_APP_PUBLIC_BASE_URL || '').trim().replace(/\/+$/, '');
    const base = configuredPublicBase || window.location.origin;
    return `${base}/store/${merchantId}`;
  })();

  const captureQrCardImage = async () => {
    if (!qrCardRef.current) return '';
    // Ensure layout (including long restaurant name wrapping) is fully painted before capture.
    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => requestAnimationFrame(resolve));
    const width = Math.ceil(qrCardRef.current.scrollWidth);
    const height = Math.ceil(qrCardRef.current.scrollHeight);
    const canvas = await html2canvas(qrCardRef.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      width,
      height,
      windowWidth: Math.max(window.innerWidth, width + 32),
    });
    return canvas.toDataURL('image/png');
  };

  const downloadDataUrl = (dataUrl, filename) => {
    const link = document.createElement('a');
    link.download = filename;
    link.href = dataUrl;
    link.click();
  };

  const handleDownloadQr = async () => {
    try {
      const dataUrl = await captureQrCardImage();
      if (!dataUrl) return;
      downloadDataUrl(dataUrl, `${restaurantName.replace(/\s+/g, '-').toLowerCase()}-store-qr.png`);
      setShareMessage('Branded QR image downloaded.');
    } catch {
      setShareMessage('Unable to download QR image right now.');
    }
  };

  const handleCopyStoreLink = async () => {
    try {
      await navigator.clipboard.writeText(storeUrl);
      setShareMessage('Store link copied. You are ready to share.');
    } catch {
      setShareMessage('Unable to copy store link.');
    }
  };

  const handleWhatsAppShare = () => {
    const waUrl = `https://wa.me/?text=${encodeURIComponent(`Join our loyalty program: ${storeUrl}`)}`;
    window.open(waUrl, '_blank', 'noopener,noreferrer');
    setShareMessage('WhatsApp opened with your store link.');
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const blob = await apiDownloadInvoicePdf(5);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${restaurantName.replace(/\s+/g, '-').toLowerCase()}-qr-pack.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setShareMessage('QR pack PDF downloaded.');
    } catch {
      setShareMessage('Unable to generate QR PDF right now.');
    }
    setDownloadingPdf(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl rounded-2xl p-0 overflow-hidden border-0" data-testid="qr-code-modal">
        <div className="bg-white">
          <div className="px-8 pt-8 pb-4 border-b border-stone-100">
            <p className="text-sm font-semibold text-brand">FlyBack</p>
          </div>

          <div className="text-center px-8 pt-6 pb-2">
            <DialogTitle className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
              Your Store QR Code
            </DialogTitle>
            <DialogDescription className="text-sm text-stone-500 mt-2">
              Turn every visit into rewards. Scan. Dine. Earn points instantly.
            </DialogDescription>
          </div>

          <div className="grid md:grid-cols-2 gap-6 p-8">
            <div className="space-y-4">
              <div className="bg-stone-50 rounded-2xl p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-brand-soft flex items-center justify-center"><Sparkles className="w-4 h-4 text-brand" /></div>
                  <h3 className="font-bold text-sm">Usage</h3>
                </div>
                <p className="text-sm text-stone-500">Place this QR near tables and checkout to convert walk-ins into repeat loyalty members.</p>
                <ul className="space-y-2.5">
                  {['Restaurant-specific guest entry', 'Customers earn points every order', 'Fast copy-link + WhatsApp sharing'].map((t) => (
                    <li key={t} className="flex items-center gap-2.5 text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />{t}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex flex-col items-center space-y-4">
              <div
                ref={qrCardRef}
                className="w-[360px] max-w-full bg-white border border-stone-200 rounded-none p-6 shadow-sm"
                data-testid="qr-share-card"
              >
                <div className="flex items-center gap-3">
                  <img src={restaurantLogo} alt={`${restaurantName} logo`} className="w-12 h-12 rounded-xl object-cover border border-stone-200" />
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-widest text-stone-400">Restaurant</p>
                    <p className="text-sm font-bold leading-tight break-words">{restaurantName}</p>
                  </div>
                </div>
                <div className="mt-4 flex justify-center">
                  <div className="bg-white rounded-none p-3 border border-stone-200">
                    <QRCodeCanvas
                      value={storeUrl}
                      size={210}
                      level="H"
                      fgColor="#1c1917"
                      bgColor="white"
                      data-testid="qr-code-svg"
                    />
                  </div>
                </div>
                <p className="text-center text-xs text-brand font-semibold mt-3">Scan. Dine. Earn Rewards.</p>
                <p className="text-center text-[11px] text-stone-500 mt-1">Join now and unlock exclusive points and perks.</p>
                <img src="/assets/flyback.png" alt="FlyBack" className="h-6 w-auto object-contain mx-auto mt-4" />
                <p className="text-center text-[11px] text-stone-400 mt-2">Powered by FlyBack</p>
              </div>

              <div className="grid grid-cols-2 gap-3 w-full">
                <button
                  data-testid="download-qr-btn"
                  onClick={handleDownloadQr}
                  className="h-11 rounded-xl gradient-brand-btn font-semibold text-sm flex items-center justify-center gap-1.5"
                >
                  <Download className="w-4 h-4" /> Download QR
                </button>
                <button
                  data-testid="copy-store-link-btn"
                  onClick={handleCopyStoreLink}
                  className="h-11 rounded-xl border border-stone-200 bg-white font-semibold text-sm flex items-center justify-center gap-1.5 hover:bg-stone-50"
                >
                  <Copy className="w-4 h-4" /> Copy Link
                </button>
                <button
                  data-testid="share-whatsapp-btn"
                  onClick={handleWhatsAppShare}
                  className="h-11 rounded-xl border border-stone-200 bg-white font-semibold text-sm flex items-center justify-center gap-1.5 hover:bg-stone-50"
                >
                  <Share2 className="w-4 h-4" /> WhatsApp
                </button>
                <button
                  data-testid="download-qr-pdf-btn"
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                  className="h-11 rounded-xl border border-stone-200 bg-white font-semibold text-sm flex items-center justify-center gap-1.5 hover:bg-stone-50 disabled:opacity-50"
                >
                  <FileText className="w-4 h-4" /> {downloadingPdf ? 'Generating...' : 'Download Invoice PDF'}
                </button>
              </div>
              {shareMessage && (
                <p className="text-xs text-emerald-600 font-semibold text-center" data-testid="qr-share-message">
                  {shareMessage}
                </p>
              )}
            </div>
          </div>

          <div className="px-8 py-4 border-t border-stone-100 text-center text-xs text-stone-400 italic">
            Powered by FlyBack
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
